import { getCompany, patchCompany } from '../../api/company/company';

/**
 * Utility functions for checking and enforcing package limits
 */

function getCurrentCounts(company) {
    return {
        employees:
            company?.resourceCounts?.employees ??
            company?.resourceCounts?.employeesCount ??
            company?.employeesCount ??
            0,
        appointments:
            company?.resourceCounts?.appointments ??
            company?.resourceCounts?.appointmentsCount ??
            company?.appointmentsCount ??
            0,
    };
}

export const getEmployeeCapacity = (company, currentEmployeeCount) => {
    const limits = company?.package?.limits;
    const currentCounts = getCurrentCounts(company);
    const current =
        typeof currentEmployeeCount === 'number'
            ? currentEmployeeCount
            : currentCounts.employees;

    if (!limits || limits.employees === -1) {
        return {
            unlimited: true,
            current,
            max: null,
            remaining: Number.POSITIVE_INFINITY,
        };
    }

    const max = limits.employees;
    return {
        unlimited: false,
        current,
        max,
        remaining: Math.max(0, max - current),
    };
};

export const canAddEmployees = (company, countToAdd = 1, currentEmployeeCount) => {
    const capacity = getEmployeeCapacity(company, currentEmployeeCount);
    if (capacity.unlimited) return true;
    return capacity.remaining >= countToAdd;
};

// Check if a company can add more of a specific resource
export const canAddResource = (company, resourceType, currentEmployeeCount) => {
    if (!company?.package?.limits) return false;

    const currentCounts = getCurrentCounts(company);
    if (typeof currentEmployeeCount === 'number') {
        currentCounts.employees = currentEmployeeCount;
    }

    const limits = company.package.limits;

    switch (resourceType) {
        case 'employee':
            if (limits.employees === -1) return true;
            return currentCounts.employees < limits.employees;
        case 'appointment':
            return true;
        default:
            return false;
    }
};

// Get remaining resources for a company
export const getRemainingResources = (company, currentEmployeeCount) => {
    if (!company?.package?.limits) return { employees: 0, appointments: 0 };

    const currentCounts = getCurrentCounts(company);
    if (typeof currentEmployeeCount === 'number') {
        currentCounts.employees = currentEmployeeCount;
    }

    const limits = company.package.limits;

    return {
        employees:
            limits.employees === -1
                ? Number.POSITIVE_INFINITY
                : Math.max(0, limits.employees - currentCounts.employees),
        appointments: currentCounts.appointments,
    };
};

// Check if a company is approaching any limits (80% threshold)
export const checkLimitWarnings = (company, currentEmployeeCount) => {
    if (!company?.package?.limits) return [];

    const warnings = [];
    const threshold = 0.8;

    const currentCounts = getCurrentCounts(company);
    if (typeof currentEmployeeCount === 'number') {
        currentCounts.employees = currentEmployeeCount;
    }

    const limits = company.package.limits;

    if (limits.employees !== -1 && currentCounts.employees / limits.employees >= threshold) {
        warnings.push('employee');
    }

    return warnings;
};

// Format the package limits for display
export const formatPackageLimits = (company, currentEmployeeCount) => {
    if (!company?.package?.limits) return null;

    const currentCounts = getCurrentCounts(company);
    if (typeof currentEmployeeCount === 'number') {
        currentCounts.employees = currentEmployeeCount;
    }

    return {
        name: company.package.name,
        limits: {
            employees: {
                current: currentCounts.employees,
                max: company.package.limits.employees,
                name: 'Employees'
            },
            appointments: {
                current: currentCounts.appointments,
                max: 'Unlimited',
                name: 'Appointments'
            }
        }
    };
};

function nextCount(company, resourceType, delta) {
    const counts = getCurrentCounts(company);
    const key = resourceType === 'employee' ? 'employees' : resourceType;
    const current = Number(counts[key] || 0);
    return Math.max(0, current + delta);
}

async function patchResourceCount(companyId, resourceType, delta, company) {
    const current = company || (await getCompany({ companyId }));
    const next = nextCount(current, resourceType, delta);
    const countKey = resourceType === 'employee' ? 'employeesCount' : `${resourceType}Count`;
    const resourceKey = resourceType === 'employee' ? 'employees' : resourceType;
    const patched = await patchCompany({
        companyId,
        company: {
            resourceCounts: {
                [resourceType]: next,
                [resourceKey]: next,
                [countKey]: next,
            },
            [countKey]: next,
        },
    });
    return patched;
}

// Helper function to increment a resource count
export const incrementResourceCount = async (companyId, resourceType, company) => {
    try {
        await patchResourceCount(companyId, resourceType, 1, company);
        return true;
    } catch (error) {
        console.error(`Error incrementing ${resourceType} count:`, error);
        return false;
    }
};

// Helper function to decrement a resource count
export const decrementResourceCount = async (companyId, resourceType, company) => {
    try {
        await patchResourceCount(companyId, resourceType, -1, company);
        return true;
    } catch (error) {
        console.error(`Error decrementing ${resourceType} count:`, error);
        return false;
    }
}; 